import { useState, useRef } from 'react';
import Button from '@mui/material/Button';
import {
    ButtonGroup,
    FormGroup,
    Typography
} from '@mui/material';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import PutPostPatchFetch from '../../hooks/PutPostPatchFetch.js';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LoadingIndicatorPageLength } from '../../components/Loading';
import CostTypeDropdown from './CostTypeDropdown.js';
import BaseNotification from '../../components/BaseNotification/BaseNotification.js';
import BuildingMultiselectDevEx from '../TotalBuildingCostReportComponents/BuildingMultiselectDevEx.js';
import RegionMultiSelectDevEx from '../TotalBuildingCostReportComponents/RegionMultiSelectDevEx.js';


const dateFieldSx = {
    mr: 1,
    width: 100
};

const SearchButtonGroupCostEstimates = ({ setter }) => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedBldgNumber, setSelectedBldgNumber] = useState([]);
    const [selectedCostType, setSelectedCostType] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [selectedRegions, setSelectedRegions] = useState([]);
    const [notification, setNotification] = useState({
        message: '',
        type: undefined,
        visible: false
    });


    const handle_setSelectValue_BldgSequenceNo = (value) => {
        setSelectedBldgNumber(value);
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearch();
        };
    };

    const handleSearch = async () => {
        setNotification({
            message: '',
            type: undefined,
            visible: false
        });

        const startDateFormat = new Date(startDate.format('MM/DD/YYYY'));
        const endDateFormat = new Date(endDate.format('MM/DD/YYYY'));

        if (!startDate || !endDate) {
            setNotification({
                message: "Please ensure that the date range you entered follows the correct syntax. The date range should be in the format of 'MM/YYYY - MM/YYYY.' For example, '01/2023 - 12/2023' represents a valid date range for the fiscal year. Double-check your input and try again. If you continue to experience issues, please contact our support team for assistance.",
                type: 'error',
                visible: true
            });
            return;
        };

        if (startDate && endDate && startDateFormat.valueOf() > endDateFormat.valueOf()) {
            setNotification({
                message: 'Start Date and End Date must follow one of the appropraite sequences: Start Date must be equal to or come before the End Date. End Date must be equal to or come after the Start date. Please fix your dates and try again.',
                type: 'error',
                visible: true
            });
            return;
        };

        if (isNaN(startDateFormat.getDate()) || isNaN(endDateFormat.getDate())) {
            setNotification({
                message: "Both Start Date and End Date values must be fully entered as MM-YYYY format. Please fix your values and try again.",
                type: 'error',
                visible: true
            });
            return;
        };

        setter(null);
        setLoadingData(true);

        const buildingNumbers = selectedBldgNumber === null ? [] : selectedBldgNumber.toString();

        try {
            let searchParameters = {
                StartDate: startDateFormat,
                EndDate: endDateFormat,
                CostTypes: selectedCostType,
                BuildingNameDomains: buildingNumbers.length === 0 ? null : buildingNumbers.replace(/,$/, "").split(","),
                RegionNumbers: selectedRegions
            }

            const {
                Errors,
                Message,
                Success
            } = await PutPostPatchFetch('/v1/CostEstimateChangesReportByFiscalYearReport/CostChargesEstimate', 'POST', searchParameters);

            if (Success) {
                setter(Message);
            }

            if (Errors) {
                if (Errors.length > 0) {
                    setNotification({
                        message: Errors[0].Message || "No records found for this building. Please select a different building.",
                        type: 'error',
                        visible: true
                    });
                    setter(null);
                }
            }

        } catch (error) {
            setNotification({
                message: error || "One or more errors occurred. Please correct errors and try again.",
                type: 'error',
                visible: true
            });
            setter(null);
        }
        setLoadingData(false);
    };

    return (
        <FormGroup sx={{ width: '97%', margin: 'auto' }}>
            <Typography
                variant="body1"
                component="em"
                style={{ color: "#06152B" }}
            >
                Search by Date Range, Building Number, and Cost Type
            </Typography>
            <ButtonGroup
                disableElevation
                variant="contained"
                sx={{ my: 1 }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateField
                        sx={dateFieldSx}
                        id="start-date"
                        label="Start Date"
                        format="MM-YYYY"
                        value={startDate}
                        onChange={(newValue) => { setStartDate(newValue) }}
                        inputProps={{
                            onKeyDown: handleKeyDown,
                            "aria-required": "true"
                        }}
                        InputLabelProps={{
                            style: { color: 'black' }
                        }}
                        error={!startDate}
                        helperText={"Required *"}
                    />
                    <DateField
                        id="end-date"
                        sx={dateFieldSx}
                        label="End Date"
                        format="MM-YYYY"
                        value={endDate}
                        onChange={(newValue) => { setEndDate(newValue) }}
                        inputProps={{
                            onKeyDown: handleKeyDown,
                            "aria-required": "true"
                        }}
                        InputLabelProps={{
                            style: { color: 'black' }
                        }}
                        error={!endDate}
                        helperText={"Required *"}
                    />
                </LocalizationProvider>
                <span>&nbsp;</span>
                <RegionMultiSelectDevEx
                    value={selectedRegions}
                    valueSetter={setSelectedRegions} />

                <span>&nbsp;</span>
                <BuildingMultiselectDevEx
                    value={selectedBldgNumber}
                    valueSetter={handle_setSelectValue_BldgSequenceNo}
                    selectedRgns={selectedRegions}
                    dependentOnRegion={true}
                    allAllowed={false}
                    req={false}

                />


                <CostTypeDropdown
                    selection={selectedCostType}
                    setSelection={setSelectedCostType}
                    handleKeyDown={handleKeyDown}
                />

                <Button
                    id="submit-button"
                    variant="contained"
                    disabled={!startDate || !endDate || (selectedRegions && selectedRegions.length < 1)}

                    onClick={handleSearch}
                    startIcon={<QueryStatsRoundedIcon />}
                >
                    Find Changes
                </Button>
            </ButtonGroup>
            {loadingData &&
                <LoadingIndicatorPageLength message={"searching for records..."} />
            }
            <BaseNotification
                message={notification.message}
                ref={useRef(null)}
                setter={() => setNotification(() => (
                    {
                        isVisible: false,
                        message: '',
                        type: undefined
                    }
                ))}
                type={notification.type}
                visible={notification.visible}
            />
        </FormGroup>
    );
}

export default SearchButtonGroupCostEstimates;